function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import * as React from 'react';
import useEventCallback from '@mui/utils/useEventCallback';
import { unstable_useControlled as useControlled } from '@mui/utils';
var warnedOnceNotValidView = false;
export function useViews(_ref) {
  var _views, _views2;
  var onChange = _ref.onChange,
    onViewChange = _ref.onViewChange,
    openTo = _ref.openTo,
    inView = _ref.view,
    views = _ref.views,
    autoFocus = _ref.autoFocus,
    inFocusedView = _ref.focusedView,
    onFocusedViewChange = _ref.onFocusedViewChange;
  if (process.env.NODE_ENV !== 'production') {
    if (!warnedOnceNotValidView) {
      if (inView != null && !views.includes(inView)) {
        console.warn("MUI X: `view=\"".concat(inView, "\"` is not a valid prop."), "It must be an element of `views=[\"".concat(views.join('", "'), "\"]`."));
        warnedOnceNotValidView = true;
      }
      if (inView == null && openTo != null && !views.includes(openTo)) {
        console.warn("MUI X: `openTo=\"".concat(openTo, "\"` is not a valid prop."), "It must be an element of `views=[\"".concat(views.join('", "'), "\"]`."));
        warnedOnceNotValidView = true;
      }
    }
  }
  var previousOpenTo = React.useRef(openTo);
  var previousViews = React.useRef(views);
  var defaultView = React.useRef(views.includes(openTo) ? openTo : views[0]);
  var _useControlled = useControlled({
      name: 'useViews',
      state: 'view',
      controlled: inView,
      default: defaultView.current
    }),
    _useControlled2 = _slicedToArray(_useControlled, 2),
    view = _useControlled2[0],
    setView = _useControlled2[1];
  var defaultFocusedView = React.useRef(autoFocus ? view : null);
  var _useControlled3 = useControlled({
      name: 'useViews',
      state: 'focusedView',
      controlled: inFocusedView,
      default: defaultFocusedView.current
    }),
    _useControlled4 = _slicedToArray(_useControlled3, 2),
    focusedView = _useControlled4[0],
    setFocusedView = _useControlled4[1];
  React.useEffect(function () {
    // Update the current view when `openTo` or `views` props change
    if (previousOpenTo.current && previousOpenTo.current !== openTo || previousViews.current && previousViews.current.some(function (previousView) {
      return !views.includes(previousView);
    })) {
      setView(views.includes(openTo) ? openTo : views[0]);
      previousViews.current = views;
      previousOpenTo.current = openTo;
    }
  }, [openTo, setView, view, views]);
  var viewIndex = views.indexOf(view);
  var previousView = (_views = views[viewIndex - 1]) !== null && _views !== void 0 ? _views : null;
  var nextView = (_views2 = views[viewIndex + 1]) !== null && _views2 !== void 0 ? _views2 : null;
  var handleFocusedViewChange = useEventCallback(function (viewToFocus, hasFocus) {
    if (hasFocus) {
      // Focus event
      setFocusedView(viewToFocus);
    } else {
      // Blur event
      setFocusedView(function (prevFocusedView) {
        return viewToFocus === prevFocusedView ? null : prevFocusedView;
      } // If false the blur is due to view switching
      );
    }
    onFocusedViewChange === null || onFocusedViewChange === void 0 || onFocusedViewChange(viewToFocus, hasFocus);
  });
  var handleChangeView = useEventCallback(function (newView) {
    // always keep the focused view in sync
    handleFocusedViewChange(newView, true);
    if (newView === view) {
      return;
    }
    setView(newView);
    if (onViewChange) {
      onViewChange(newView);
    }
  });
  var goToNextView = useEventCallback(function () {
    if (nextView) {
      handleChangeView(nextView);
    }
  });
  var setValueAndGoToNextView = useEventCallback(function (value, currentViewSelectionState, selectedView) {
    var isSelectionFinishedOnCurrentView = currentViewSelectionState === 'finish';
    var hasMoreViews = selectedView ?
    // handles case like `DateTimePicker`, where a view might return a `finish` selection state
    // but when it's not the final view given all `views` -> overall selection state should be `partial`.
    views.indexOf(selectedView) < views.length - 1 : Boolean(nextView);
    var globalSelectionState = isSelectionFinishedOnCurrentView && hasMoreViews ? 'partial' : currentViewSelectionState;
    onChange(value, globalSelectionState, selectedView);
    // Detects if the selected view is not the active one.
    // Can happen if multiple views are displayed, like in `DesktopDateTimePicker` or `MultiSectionDigitalClock`.
    if (selectedView && selectedView !== view) {
      var nextViewAfterSelected = views[views.indexOf(selectedView) + 1];
      if (nextViewAfterSelected) {
        // move to next view after the selected one
        handleChangeView(nextViewAfterSelected);
      }
    } else if (isSelectionFinishedOnCurrentView) {
      goToNextView();
    }
  });
  return {
    view: view,
    setView: handleChangeView,
    focusedView: focusedView,
    setFocusedView: handleFocusedViewChange,
    nextView: nextView,
    previousView: previousView,
    // Always return up-to-date default view instead of the initial one (i.e. defaultView.current)
    defaultView: views.includes(openTo) ? openTo : views[0],
    goToNextView: goToNextView,
    setValueAndGoToNextView: setValueAndGoToNextView
  };
}